import { useEffect, useState } from 'react';
import { useTranslation, getSessionStorage } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useWebsiteBanner } from '@shared/components/WebsiteBanner';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useInAppBrowser, openInExternalBrowser } from '@shared/core/inAppBrowser';
import { useGuestSiteTelemetry } from '@apps/guest/GuestSite.telemetry';

const DISMISSED_KEY = 'guest.inAppBrowserBannerDismissed';

type UseInAppBrowserBannerArgs = Readonly<{
  eventId?: string;
}>;

/**
 * Registers a dismissible, mobile-only banner that nudges guests opening the link
 * inside an in-app browser (WhatsApp, Instagram, etc.) to open it in a real browser.
 * Gated behind the `guestInAppBrowserBanner` flag. Dismissal is session-scoped per event.
 */
export const useInAppBrowserBanner = ({ eventId }: UseInAppBrowserBannerArgs) => {
  const { tq } = useTranslation('guestInAppBrowserBanner');
  const isMobile = useIsMobileScreen();
  const info = useInAppBrowser();
  const telemetry = useGuestSiteTelemetry();
  const flagOn = useFeatureValue('guestInAppBrowserBanner').value === 'on';

  const storageKey = eventId ? `${DISMISSED_KEY}.${eventId}` : undefined;
  const [dismissed, setDismissed] = useState<boolean>(() => (storageKey ? getSessionStorage().getItem(storageKey) === 'true' : false));

  // `eventId` arrives asynchronously (GraphQL), so `storageKey` is undefined on the first
  // render and the initializer above can't read the per-event dismissal. Re-sync once it loads
  // so a guest who already dismissed this session doesn't see the banner again on reload.
  useEffect(() => {
    if (storageKey && getSessionStorage().getItem(storageKey) === 'true') {
      setDismissed(true);
    }
  }, [storageKey]);

  // Require `storageKey` (i.e. `eventId` has loaded) so a dismissal always persists —
  // otherwise a guest could dismiss during the initial load and see the banner again on reload.
  const isVisible = !!storageKey && flagOn && isMobile && info.isInAppBrowser && !dismissed;

  useEffect(() => {
    if (isVisible) {
      telemetry.inAppBrowserBannerChange('Shown', info.app ?? 'unknown', info.platform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleOpen = useEventCallback(() => {
    telemetry.inAppBrowserBannerChange('OpenClicked', info.app ?? 'unknown', info.platform);
    withWindow(({ location }) => openInExternalBrowser(info, location.href));
  });

  const handleClose = useEventCallback(() => {
    if (storageKey) {
      getSessionStorage().setItem(storageKey, 'true');
    }
    setDismissed(true);
    telemetry.inAppBrowserBannerChange('Dismissed', info.app ?? 'unknown', info.platform);
  });

  useWebsiteBanner({
    id: 'in-app-browser',
    isVisible,
    message: {
      default: tq`For the best experience, open this page in your browser.`,
    },
    actionButton: {
      label: { default: tq`Open` },
      onClick: handleOpen,
    },
    onClose: handleClose,
    closeButtonPlacement: 'left',
  });
};
